import { useEffect, useState } from 'react';
import { buildInfo, buildInfoString } from '../lib/buildInfo';

export type AboutActionKind =
  | 'feedback'
  | 'update'
  | 'terms'
  | 'privacy'
  | 'contact';

export type AxiSettingsAboutSectionProps = {
  /**
   * Optional consumer hook invoked once after every internal action is
   * handled (version copy, every button click). The default is a no-op so
   * the section can be mounted without ceremony. `kind` identifies which
   * action fired; the version card carries an empty string because it is
   * not a numeric `ActionKind`.
   */
  onAction?: (
    kind: AboutActionKind | { kind: 'copy-version'; ok: boolean }
  ) => void;
};

type Toast = { text: string; ts: number };

const ACTION_BUTTONS: ReadonlyArray<{
  kind: AboutActionKind;
  label: string;
  primary?: boolean;
}> = [
  { kind: 'feedback', label: '意见反馈', primary: true },
  { kind: 'update', label: '检查更新' },
  { kind: 'terms', label: '用户协议' },
  { kind: 'privacy', label: '隐私政策' },
  { kind: 'contact', label: '联系我们' }
];

/**
 * 「关于我们 / About」section. Renders after `AxiSettingsThemeSection`
 * inside the settings panel body. Shows logo + product name + slogan, a
 * tappable version card (version / Build / Patch, copies the canonical
 * string to the clipboard), and five vertical action buttons with
 * 意见反馈 (feedback) as the Primary button per the NetEase Cloud Music
 * SOP reference design. A small toast surfaces the result of every
 * interaction for 1.5s.
 *
 * The component is self-contained: no external props, no provider, no
 * store. `onAction` is purely advisory (telemetry / future hand-off).
 */
function AxiSettingsAboutSection({
  onAction
}: AxiSettingsAboutSectionProps = {}) {
  const [toast, setToast] = useState<Toast | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 1500);
    return () => window.clearTimeout(t);
  }, [toast]);

  const showToast = (text: string) =>
    setToast({ text, ts: Date.now() });

  const handleCopyVersion = async () => {
    const payload = buildInfoString();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
        showToast('已复制版本信息');
        onAction?.({ kind: 'copy-version', ok: true });
      } else {
        showToast('复制失败,请手动选择文本');
        onAction?.({ kind: 'copy-version', ok: false });
      }
    } catch {
      showToast('复制失败,请手动选择文本');
      onAction?.({ kind: 'copy-version', ok: false });
    }
  };

  const handleAction = (kind: AboutActionKind) => {
    if (kind === 'feedback') {
      // Carry the canonical version string into the (future) feedback
      // hand-off. Today we log + toast — the wired feedback system is
      // out of scope for this spec.
      // eslint-disable-next-line no-console
      console.info('[feedback]', buildInfoString());
      showToast('已携带版本信息,反馈功能即将上线');
    } else {
      const label = ACTION_BUTTONS.find((b) => b.kind === kind)?.label ?? kind;
      showToast(`「${label}」即将上线`);
    }
    onAction?.(kind);
  };

  return (
    <section
      className="axi-settings-section axi-settings-about"
      aria-labelledby="axi-settings-about-title"
    >
      <h3
        id="axi-settings-about-title"
        className="axi-settings-section__title"
      >
        关于我们 / About
      </h3>
      <div className="axi-settings-about__inner">
        <header className="axi-settings-about__header">
          <svg
            className="axi-settings-about__logo"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            aria-hidden="true"
          >
            <path
              d="M12 2 L22 12 L12 22 L2 12 Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="12" r="3" fill="currentColor" />
          </svg>
          <span className="axi-settings-about__brand">
            <span className="axi-settings-about__name">Axi Design</span>
            <span className="axi-settings-about__slogan">
              AxiomaticWorld 设计系统预览
            </span>
          </span>
        </header>

        <button
          type="button"
          className="axi-settings-about__version"
          aria-label="复制版本信息"
          onClick={handleCopyVersion}
        >
          <dl className="axi-settings-about__version-list">
            <div className="axi-settings-about__version-row">
              <dt>版本</dt>
              <dd data-testid="about-version">{buildInfo.version}</dd>
            </div>
            <div className="axi-settings-about__version-row">
              <dt>Build</dt>
              <dd data-testid="about-build">{buildInfo.build}</dd>
            </div>
            <div className="axi-settings-about__version-row">
              <dt>Patch</dt>
              <dd data-testid="about-patch">{buildInfo.patch}</dd>
            </div>
          </dl>
        </button>

        <ul className="axi-settings-about__list">
          {ACTION_BUTTONS.map(({ kind, label, primary }) => (
            <li key={kind}>
              <button
                type="button"
                className={
                  'axi-settings-about__row' + (primary ? ' is-primary' : '')
                }
                onClick={() => handleAction(kind)}
              >
                <span className="axi-settings-about__row-label">{label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="axi-settings-about__toast"
          onClick={() => setToast(null)}
        >
          {toast.text}
        </div>
      )}
    </section>
  );
}

export default AxiSettingsAboutSection;
